import React, { createContext, useContext, useState, useEffect } from "react";

export type Theme = "dark" | "light";

interface ThemeContextType {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const MarketplaceThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    return (localStorage.getItem("marketplace-theme") as Theme) || "dark";
  });

  const toggleTheme = () => {
    const nextTheme = theme === "dark" ? "light" : "dark";
    setTheme(nextTheme);
    localStorage.setItem("marketplace-theme", nextTheme);
    window.dispatchEvent(new CustomEvent("marketplace-theme-change", { detail: nextTheme }));
  };

  useEffect(() => {
    document.documentElement.setAttribute("data-marketplace-theme", theme);
    const styles = themeStyles[theme];
    const root = document.documentElement;
    root.style.setProperty("--mp-background", styles.background);
    root.style.setProperty("--mp-border", styles.border);
    root.style.setProperty("--mp-textColor", styles.textColor);
    root.style.setProperty("--mp-bodyBackground", styles.bodyBackground);
    root.style.setProperty("--mp-cardBackground", styles.cardBackground);
    root.style.setProperty("--mp-sidebarBackground", styles.sidebarBackground);
    root.style.setProperty("--mp-sidebarBorder", styles.sidebarBorder);
    root.style.setProperty("--mp-heroBackground", styles.heroBackground);
    root.style.setProperty("--mp-headingColor", styles.headingColor);
    root.style.setProperty("--mp-subColor", styles.subColor);
    root.style.setProperty("--mp-heroAccent", styles.heroAccent);
    root.style.setProperty("--mp-heroBadgeBg", styles.heroBadgeBg);
    root.style.setProperty("--mp-heroBadgeBorder", styles.heroBadgeBorder);
    root.style.setProperty("--mp-heroBtnBg", styles.heroBtnBg);
    root.style.setProperty("--mp-heroBtnColor", styles.heroBtnColor);
    root.style.setProperty("--mp-heroHeadingColor", styles.heroHeadingColor);
    root.style.setProperty("--mp-heroSubColor", styles.heroSubColor);
    root.style.setProperty("--mp-heroOverlay", styles.heroOverlay);
    root.style.setProperty("--mp-featureIconColor", styles.featureIconColor);
    root.style.setProperty("--mp-featureDescColor", styles.featureDescColor);
    root.style.setProperty("--mp-iconGrad", styles.iconGrad);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useMarketplaceTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useMarketplaceTheme must be used within a MarketplaceThemeProvider");
  }
  return context;
};

export const themeStyles = {
  dark: {
    background: "#050506",
    border: "rgba(255, 255, 255, 0.35)",
    tabColor: "#D2C5AF",
    textColor: "#ffffff",
    bodyBackground: "#0b0c0d",
    cardBackground: "#15181b",
    sidebarBackground: "#121416",
    sidebarBorder: "rgba(255, 255, 255, 0.08)",
    heroBackground: "linear-gradient(135deg, rgba(18,20,22,0.95) 0%, rgba(21,24,27,0.98) 100%)",
    headingColor: "#ffffff",
    subColor: "rgba(255, 255, 255, 0.7)",
    heroAccent: "linear-gradient(95.57deg, #FFC951 0%, #C94DAA 100%)",
    heroBadgeBg: "#FFC9511A",
    heroBadgeBorder: "#FFC9514D",
    heroBtnBg: "linear-gradient(106.11deg, #FFC951 0%, #C94DAA 100%)",
    heroBtnColor: "#ffffff",
    heroHeadingColor: "#E2E2E5",
    heroSubColor: "#D2C5AF",
    heroOverlay: "rgba(18, 20, 22, 0.55)",
    featureIconColor: "#C94DAA",
    featureDescColor: "#D2C5AF",
    iconGrad: "linear-gradient(135deg, #FFC951 0%, #C94DAA 100%)"
  },
  light: {
    background: "#ffffff",
    border: "rgba(23, 23, 23, 0.35)",
    tabColor: "#434655",
    textColor: "#171717",
    bodyBackground: "#f7f8fc",
    cardBackground: "#ffffff",
    sidebarBackground: "#ffffff",
    sidebarBorder: "rgba(23, 23, 23, 0.08)",
    heroBackground: "linear-gradient(135deg, rgba(255,213,115,0.12) 0%, rgba(216,99,186,0.12) 50%, rgba(255,201,81,0.08) 100%)",
    headingColor: "#171717",
    subColor: "rgba(23,23,23,0.7)",
    heroAccent: "linear-gradient(95.57deg, #FFD573 0%, #D863BA 100%)",
    heroBadgeBg: "rgba(255, 213, 115, 0.25)",
    heroBadgeBorder: "rgba(216, 99, 186, 0.65)",
    heroBtnBg: "linear-gradient(106.11deg, #FFD573 0%, #D863BA 100%)",
    heroBtnColor: "#ffffff",
    heroHeadingColor: "#171717",
    heroSubColor: "#434655",
    heroOverlay: "rgba(255, 255, 255, 0.45)",
    featureIconColor: "#D863BA",
    featureDescColor: "#434655",
    iconGrad: "linear-gradient(135deg, #FFD573 0%, #D863BA 100%)"
  }
};
